import {
  useWallets,
  useSignAndSendTransaction,
} from "@privy-io/react-auth/solana";
import { usePrivy, type WalletWithMetadata } from "@privy-io/react-auth";
import { useMemo } from "react";
import bs58 from "bs58";
import configs from "@/lib/configs";

/**
 * Hook that provides a function to sign and broadcast base64-encoded
 * Solana transactions using the user's connected wallet.
 * Uses external wallet (e.g. Phantom) if available, otherwise falls back
 * to the Privy embedded wallet (social login users).
 */
export function useGameSigner() {
  const { signAndSendTransaction } = useSignAndSendTransaction();
  const { wallets } = useWallets();
  const { user } = usePrivy();

  const activeWalletAddress = useMemo(() => {
    // Prefer external wallet (e.g. Phantom) if connected
    const externalWallet = user?.linkedAccounts?.find(
      (account): account is WalletWithMetadata =>
        account.type === "wallet" &&
        account.walletClientType !== "privy" &&
        account.chainType === "solana",
    );
    if (externalWallet) return externalWallet.address;

    // Fall back to Privy embedded wallet (social login users)
    return user?.linkedAccounts?.find(
      (account): account is WalletWithMetadata =>
        account.type === "wallet" &&
        account.walletClientType === "privy" &&
        account.chainType === "solana",
    )?.address;
  }, [user?.linkedAccounts]);

  const signAndBroadcast = async (base64Tx: string): Promise<string> => {
    const wallet = wallets.find((w) => w.address === activeWalletAddress);
    if (!wallet) {
      throw new Error("No wallet found. Please try again.");
    }

    const txBytes = Uint8Array.from(atob(base64Tx), (c) => c.charCodeAt(0));
    const result = await signAndSendTransaction({
      transaction: txBytes,
      wallet,
      chain: configs.IS_TESTNET ? "solana:devnet" : "solana:mainnet",
    });

    return bs58.encode(result.signature);
  };

  return { signAndBroadcast };
}
